import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { Slot } from 'radix-ui'

import { cn } from '#/lib/utils'

// The brand status mark: a plain word, no fill, no border, no dot — the word
// carries the meaning and its colour only leans on it (DESIGN.md §5).
const STATUS_MARK =
  'h-auto rounded-none border-transparent bg-transparent px-0 py-0 text-caption-md font-normal'

const badgeVariants = cva(
  'group/badge inline-flex h-5 w-fit shrink-0 items-center justify-center gap-1 overflow-hidden rounded-md border border-transparent px-2 py-0.5 text-xs font-medium whitespace-nowrap transition-all focus-ring has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 aria-invalid:border-destructive aria-invalid:ring-destructive/20 [&>svg]:pointer-events-none [&>svg]:size-3!',
  {
    variants: {
      variant: {
        default:
          'bg-primary text-primary-foreground [a]:hover:bg-primary-hover',
        secondary:
          'bg-secondary text-secondary-foreground [a]:hover:bg-secondary/80',
        destructive:
          'bg-destructive/10 text-destructive-text [a]:hover:bg-destructive/15',
        outline:
          'border-border text-foreground [a]:hover:bg-muted [a]:hover:text-muted-foreground',
        ghost: 'hover:bg-muted hover:text-muted-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
        // A sand chip for values and tags (a selected option, a type name).
        neutral: 'bg-muted text-muted-foreground border-border',
        // Status marks, one per meaning. Done, complete, active: ink.
        success: cn(STATUS_MARK, 'text-foreground'),
        // Under way: stone.
        info: cn(STATUS_MARK, 'text-muted-foreground'),
        // Needs a person's attention: the ember text stop.
        warning: cn(STATUS_MARK, 'text-alert-text'),
        alert: cn(STATUS_MARK, 'font-medium text-alert-text'),
        // The one red: a genuine failure.
        danger: cn(STATUS_MARK, 'text-destructive-text'),
        // Not started, new: stone.
        pending: cn(STATUS_MARK, 'text-muted-foreground'),
        // Archived, inactive: stone.
        idle: cn(STATUS_MARK, 'text-muted-foreground'),
        // The AI tone: anything Annwn drafted that a person still reviews.
        // AI work (a draft's confidence, say): the word in indigo, no chip.
        analysis: `${STATUS_MARK} text-analysis-text`,
        // Shape only, no colour — for callers that supply their own bg/text/
        // border via className.
        plain: '',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
)

function Badge({
  className,
  variant = 'default',
  asChild = false,
  ...props
}: React.ComponentProps<'span'> &
  VariantProps<typeof badgeVariants> & { asChild?: boolean }) {
  const Comp = asChild ? Slot.Root : 'span'

  return (
    <Comp
      data-slot="badge"
      data-variant={variant}
      className={cn(badgeVariants({ variant }), className)}
      {...props}
    />
  )
}

export type BadgeVariant = NonNullable<
  VariantProps<typeof badgeVariants>['variant']
>

export { Badge, badgeVariants }
